import type { HealthMetricMap, IsoDate, LogMap, Measurement } from "./types.js";
import { addDays, formatDate } from "./dates.js";
import { PLAN_END, PLAN_START, RACE_DATE_LABEL } from "./plan.js";
import { weightTrend } from "./measurements.js";
import { proteinDayDone, stepsDayDone } from "./scoring.js";

export interface ComplianceRates {
  days: number;
  workout: number | null;
  protein: number | null;
  vegetables: number | null;
  steps: number | null;
  water: number | null;
}

export interface ComplianceInput {
  logs: LogMap;
  healthMetrics: HealthMetricMap;
  today: IsoDate;
}

export type DayRates = Omit<ComplianceRates, "days">;

function ratesFor(dates: IsoDate[], logs: LogMap, healthMetrics: HealthMetricMap, empty: number | null): DayRates {
  if (!dates.length) {
    return { workout: empty, protein: empty, vegetables: empty, steps: empty, water: empty };
  }
  const rate = (predicate: (date: IsoDate) => boolean) =>
    100 * dates.filter(predicate).length / dates.length;
  return {
    workout: rate(date => !!logs[date]?.workout_done),
    protein: rate(date => proteinDayDone(logs[date] || {})),
    vegetables: rate(date => !!logs[date]?.vegetables_done),
    steps: rate(date => stepsDayDone(healthMetrics[date] || {})),
    water: rate(date => !!logs[date]?.water_done)
  };
}

/** 指定日子的達標率。賽季成績單用，它的日期範圍不是主程式的期程。 */
export function ratesForDates(dates: IsoDate[], logs: LogMap, healthMetrics: HealthMetricMap): DayRates {
  return ratesFor(dates, logs, healthMetrics, null);
}

/** 近 N 天的達標率。超出計畫期間的日子不計入。 */
export function compliance({ logs, healthMetrics, today }: ComplianceInput, days = 14): ComplianceRates {
  const end = today;
  const start = addDays(end, -days + 1);
  const dates: IsoDate[] = [];
  for (let date = start; date <= end; date = addDays(date, 1)) {
    if (date >= PLAN_START && date <= PLAN_END) dates.push(date);
  }
  if (!dates.length) {
    return { days: 0, workout: null, protein: null, vegetables: null, steps: null, water: null };
  }
  return { days: dates.length, ...ratesFor(dates, logs, healthMetrics, null) };
}

export interface WeeklyCompliance extends Omit<ComplianceRates, "days"> {
  label: string;
}

/** 以計畫起點為基準，每 7 天一段的達標率。最後一段可能不滿一週。 */
export function weeklyCompliance({ logs, healthMetrics, today }: ComplianceInput): WeeklyCompliance[] {
  const out: WeeklyCompliance[] = [];
  for (let start = PLAN_START; start <= today; start = addDays(start, 7)) {
    // 字串排序取較早的那個日期，最後一週不會超出今天
    const end = [addDays(start, 6), today].sort()[0];
    const dates: IsoDate[] = [];
    for (let date = start; date <= end; date = addDays(date, 1)) dates.push(date);
    out.push({ label: formatDate(start), ...ratesFor(dates, logs, healthMetrics, 0) } as WeeklyCompliance);
  }
  return out;
}

export interface StatusAssessment {
  label: "資料不足" | "進度偏慢" | "下降過快" | "進度正常" | "需持續觀察";
  cls: "" | "good" | "warn" | "bad";
  advice: string;
}

/**
 * 依 7 日平均的週變化判斷進度。
 * 判斷順序有意義：先擋「太慢」，再擋「太快」，兩者都不是才看預估值。
 */
export function statusAssessment(measurements: Measurement[], today: IsoDate): StatusAssessment {
  const { weeklyChange: weekly, leanChange, prediction } = weightTrend(measurements, today);

  if (weekly === null) {
    return { label: "資料不足", cls: "", advice: "先累積至少 7 天穩定的晨起測量。" };
  }
  if (weekly > -0.3) {
    return {
      label: "進度偏慢", cls: "warn",
      advice: "連續兩週都低於每週 0.3 kg 時，先把每天主食或油脂減少一小份（例如少半碗飯或少一份炸物），不用精算每一口熱量。"
    };
  }
  if (weekly < -1 || (leanChange !== null && leanChange < -1)) {
    return {
      label: "下降過快", cls: "bad",
      advice: "可能赤字過大或水分波動。先檢查力量、蛋白質與恢復狀況，不要再增加有氧。"
    };
  }
  if (prediction && prediction.predicted <= 75.2) {
    return {
      label: "進度正常", cls: "good",
      advice: `目前速度可望接近 ${RACE_DATE_LABEL} 目標，維持課表、蛋白質與步數。`
    };
  }
  return {
    label: "需持續觀察", cls: "warn",
    advice: "速度尚可，但預估值仍高於目標；先保持兩週一致執行再調整。"
  };
}
